import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useAudit } from '../hooks/useAudit';
import { format, differenceInYears } from 'date-fns';

function Modal({ onClose, onSaved }) {
  const { user } = useAuth();
  const { auditCriou } = useAudit();
  const [form, setForm] = useState({ prontuario: '', iniciais: '', data_nascimento: '', sexo: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSave() {
    setError('');
    if (!form.prontuario || !form.iniciais || !form.data_nascimento || !form.sexo) {
      setError('Preencha todos os campos.'); return;
    }
    setLoading(true);
    const { data: newPac, error } = await supabase.from('pacientes').insert({
      ...form, iniciais: form.iniciais.toUpperCase(), criado_por: user.id
    }).select('id').single();
    setLoading(false);
    if (error) { setError(error.message); return; }
    auditCriou('pacientes', newPac?.id, { iniciais: form.iniciais.toUpperCase(), prontuario: form.prontuario });
    onSaved();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Cadastrar paciente</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info">🔒 Nenhum nome completo é armazenado — conformidade LGPD.</div>
          {error && <div className="alert alert-error">⚠ {error}</div>}
          <div className="form-group">
            <label className="form-label">Nº Prontuário *</label>
            <input className="form-input" value={form.prontuario} onChange={e => set('prontuario', e.target.value)} placeholder="Ex: 123456" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Iniciais *</label>
              <input className="form-input" value={form.iniciais} onChange={e => set('iniciais', e.target.value)} placeholder="Ex: JSS" maxLength={5} style={{ textTransform: 'uppercase' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Sexo *</label>
              <select className="form-select" value={form.sexo} onChange={e => set('sexo', e.target.value)}>
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="outro">Outro</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Data de nascimento *</label>
            <input className="form-input" type="date" value={form.data_nascimento} onChange={e => set('data_nascimento', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <><span className="spinner" />Salvando...</> : 'Cadastrar paciente'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBolinhas({ status }) {
  if (!status) return null;
  const COR = {
    comorbidades: status.comorbidades === 1 ? '#22c55e' : '#f59e0b',
    preop:        status.preop        === 1 ? '#22c55e' : '#f59e0b',
    cirurgia:     status.cirurgia     === 1 ? '#22c55e' : '#f59e0b',
    posop:        status.posop        === 2 ? '#3b82f6' : '#f59e0b',
  };
  const dot = (color, title) => (
    <span title={title} style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, display: 'inline-block', flexShrink: 0 }} />
  );
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      {dot(COR.comorbidades, 'Comorbidades')}
      {dot(COR.preop, 'Pré-op')}
      {dot(COR.cirurgia, 'Cirurgia')}
      {dot(COR.posop, 'Pós-op')}
    </div>
  );
}

function SortIcon({ campo, ordenacao }) {
  if (ordenacao.campo !== campo) return <span style={{ opacity: .3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{ordenacao.dir === 'asc' ? '↑' : '↓'}</span>;
}

export default function PacientesPage() {
  const { isPesquisador } = useAuth();
  const { auditVisualizou } = useAudit();
  const navigate = useNavigate();
  const location = useLocation();
  const [pacientes, setPacientes] = useState([]);
  const [pesquisas, setPesquisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroPesquisa, setFiltroPesquisa] = useState('');
  const [ordenacao, setOrdenacao] = useState({ campo: 'iniciais', dir: 'asc' });
  const [showModal, setShowModal] = useState(false);

  async function load() {
    setLoading(true);
    const [pacRes, pesqRes, ppRes, cirRes, comorbRes, avalRes, radioRes, momRes] = await Promise.all([
      supabase.from('pacientes').select('*'),
      supabase.from('pesquisas').select('id, titulo').order('titulo'),
      supabase.from('pesquisa_pacientes').select('id, paciente_id, pesquisa_id'),
      supabase.from('cirurgias').select('pesquisa_paciente_id, data_cirurgia').order('data_cirurgia'),
      supabase.from('paciente_comorbidades').select('paciente_id, sem_comorbidades, has, diabetes, tabagismo, obesidade, osteoporose, doenca_renal, cardiopatia, outras'),
      supabase.from('avaliacoes').select('pesquisa_paciente_id, momento_id, status'),
      supabase.from('avaliacoes_radio').select('pesquisa_paciente_id, momento_id'),
      supabase.from('pesquisa_momentos').select('id, pesquisa_id, dias_offset, tipo_momento'),
    ]);
    setPesquisas(pesqRes.data || []);

    // Mapas auxiliares para cálculo de status
    const comorbMap = {};
    (comorbRes.data || []).forEach(c => { comorbMap[c.paciente_id] = c; });

    const avalMap = {};
    (avalRes.data || []).forEach(a => {
      if (!avalMap[a.pesquisa_paciente_id]) avalMap[a.pesquisa_paciente_id] = [];
      avalMap[a.pesquisa_paciente_id].push(a);
    });

    const radioMap = {};
    (radioRes.data || []).forEach(r => {
      if (!radioMap[r.pesquisa_paciente_id]) radioMap[r.pesquisa_paciente_id] = [];
      radioMap[r.pesquisa_paciente_id].push(r);
    });

    const momentosMap = {};
    (momRes.data || []).forEach(m => {
      if (!momentosMap[m.pesquisa_id]) momentosMap[m.pesquisa_id] = [];
      momentosMap[m.pesquisa_id].push(m);
    });

    const ppById = {};
    const ppMap = {};
    const statusByPaciente = {};

    (ppRes.data || []).forEach(pp => { ppById[pp.id] = pp; });

    const cirMap = {};
    (cirRes.data || []).forEach(c => {
      const pp = ppById[c.pesquisa_paciente_id];
      if (pp && !cirMap[pp.paciente_id]) cirMap[pp.paciente_id] = c.data_cirurgia;
    });

    (ppRes.data || []).forEach(pp => {
      if (!ppMap[pp.paciente_id]) ppMap[pp.paciente_id] = [];
      ppMap[pp.paciente_id].push(pp.pesquisa_id);

      const comorb = comorbMap[pp.paciente_id];
      const comorbidades = comorb
        ? (comorb.sem_comorbidades || ['has','diabetes','tabagismo','obesidade','osteoporose','doenca_renal','cardiopatia'].some(k => comorb[k]) || comorb.outras)
          ? 1 : 0
        : 0;

      const momentos = momentosMap[pp.pesquisa_id] || [];
      const preOpMoms = momentos.filter(m => m.dias_offset === null);
      const posOpMoms = momentos.filter(m => m.dias_offset !== null);
      const avspp = avalMap[pp.id] || [];
      const radiospp = radioMap[pp.id] || [];

      let preOpOk = preOpMoms.length > 0;
      for (const m of preOpMoms) {
        if (m.tipo_momento === 'score') {
          if (!avspp.some(a => a.momento_id === m.id && a.status === 'assinada')) { preOpOk = false; break; }
        } else if (m.tipo_momento === 'radio') {
          if (!radiospp.some(r => r.momento_id === m.id)) { preOpOk = false; break; }
        }
      }

      let posOpOk = posOpMoms.length > 0;
      for (const m of posOpMoms) {
        if (m.tipo_momento === 'score') {
          if (!avspp.some(a => a.momento_id === m.id && a.status === 'assinada')) { posOpOk = false; break; }
        } else if (m.tipo_momento === 'radio') {
          if (!radiospp.some(r => r.momento_id === m.id)) { posOpOk = false; break; }
        }
      }

      if (!statusByPaciente[pp.paciente_id]) statusByPaciente[pp.paciente_id] = {};
      statusByPaciente[pp.paciente_id][pp.pesquisa_id] = {
        comorbidades,
        preop: preOpOk ? 1 : 0,
        cirurgia: cirMap[pp.paciente_id] ? 1 : 0,
        posop: posOpOk ? 2 : 0,
      };
    });

    setPacientes((pacRes.data || []).map(p => ({
      ...p,
      pesquisas_ids: ppMap[p.id] || [],
      data_cirurgia: cirMap[p.id] || null,
      statusByPesquisa: statusByPaciente[p.id] || {},
    })));
    setLoading(false);
  }

  useEffect(() => {
    load();
    auditVisualizou('pacientes', null);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleOrdem(campo) {
    setOrdenacao(o => o.campo === campo
      ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' }
    );
  }

  const filtered = useMemo(() => {
    let lista = pacientes.filter(p => {
      const matchSearch = !search ||
        p.prontuario.toLowerCase().includes(search.toLowerCase()) ||
        p.iniciais.toLowerCase().includes(search.toLowerCase());
      const matchPesquisa = !filtroPesquisa ||
        p.pesquisas_ids?.includes(filtroPesquisa);
      return matchSearch && matchPesquisa;
    });

    lista = [...lista].sort((a, b) => {
      const dir = ordenacao.dir === 'asc' ? 1 : -1;
      if (ordenacao.campo === 'iniciais') return a.iniciais.localeCompare(b.iniciais) * dir;
      if (ordenacao.campo === 'prontuario') return a.prontuario.localeCompare(b.prontuario) * dir;
      if (ordenacao.campo === 'data_cirurgia') {
        const da = a.data_cirurgia || '9999';
        const db = b.data_cirurgia || '9999';
        return da.localeCompare(db) * dir;
      }
      return 0;
    });
    return lista;
  }, [pacientes, search, filtroPesquisa, ordenacao]);

  const sexoLabel = { M: 'Masc.', F: 'Fem.', outro: 'Outro' };

  function abrirPaciente(paciente) {
    navigate('/paciente', { state: { paciente } });
  }

  function thClick(campo, label) {
    return (
      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleOrdem(campo)}>
        {label}<SortIcon campo={campo} ordenacao={ordenacao} />
      </th>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pacientes</h1>
          <p>Cadastros anonimizados — prontuário e iniciais apenas</p>
        </div>
        {isPesquisador && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Cadastrar paciente</button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Lista de pacientes ({filtered.length})</h2>
          <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <input
              className="form-input"
              style={{ width: 220 }}
              placeholder="🔍 Prontuário ou iniciais..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="form-select"
              style={{ width: 180 }}
              value={filtroPesquisa}
              onChange={e => setFiltroPesquisa(e.target.value)}
            >
              <option value="">Todas as pesquisas</option>
              {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
            </select>
          </div>
        </div>
        <div className="table-wrap">
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center' }} className="text-muted text-sm">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🧑‍⚕️</div>
              <h3>Nenhum paciente encontrado</h3>
              <p>{search || filtroPesquisa ? 'Tente outros filtros.' : 'Cadastre o primeiro paciente.'}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  {thClick('prontuario', 'Prontuário')}
                  {thClick('iniciais', 'Iniciais')}
                  <th>Idade</th>
                  <th>Sexo</th>
                  {thClick('data_cirurgia', 'Data cirurgia')}
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => abrirPaciente(p)}>
                    <td><span className="mono">{p.prontuario}</span></td>
                    <td><strong>{p.iniciais}</strong></td>
                    <td>{differenceInYears(new Date(), new Date(p.data_nascimento))} anos</td>
                    <td><span className={`badge ${p.sexo === 'M' ? 'badge-blue' : p.sexo === 'F' ? 'badge-orange' : 'badge-gray'}`}>{sexoLabel[p.sexo]}</span></td>
                    <td className="text-sm">
                      {p.data_cirurgia
                        ? format(new Date(p.data_cirurgia + 'T12:00:00'), 'dd/MM/yyyy')
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {filtroPesquisa && <StatusBolinhas status={p.statusByPesquisa?.[filtroPesquisa]} />}
                    </td>
                    <td><span className="btn btn-sm btn-secondary">Ver →</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && <Modal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
